// ===========================================
// Product Categories Tab
// ===========================================
// Admin UI for managing product categories

import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Alert,
  Skeleton,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import FormModal from '../../common/FormModal';
import ConfirmDialog from '../../common/ConfirmDialog';
import ColorPickerField from '../../common/ColorPickerField';
import {
  useProductCategories,
  useCreateProductCategory,
  useUpdateProductCategory,
  useDeleteProductCategory,
  ProductCategoryItem,
} from '../../../hooks/useProducts';

// ===========================================
// HELPERS
// ===========================================

function autoSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function ColorSwatch({ color }: { color: string | null }) {
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: 0.5,
        bgcolor: color || '#9DA5AF',
        border: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}
    />
  );
}

// ===========================================
// FORM STATE
// ===========================================

interface FormData {
  name: string;
  slug: string;
  description: string;
  color: string | null;
}

const EMPTY_FORM: FormData = { name: '', slug: '', description: '', color: null };

// ===========================================
// COMPONENT
// ===========================================

export default function ProductCategoriesTab() {
  const { data: categories = [], isLoading, error } = useProductCategories();
  const createMutation = useCreateProductCategory();
  const updateMutation = useUpdateProductCategory();
  const deleteMutation = useDeleteProductCategory();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategoryItem | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<ProductCategoryItem | null>(null);

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData(EMPTY_FORM);
    setSlugManuallyEdited(false);
    setModalOpen(true);
  };

  const handleEdit = (cat: ProductCategoryItem) => {
    setEditingCategory(cat);
    setFormData({
      name: cat.name,
      slug: cat.slug,
      description: cat.description ?? '',
      color: cat.color ?? null,
    });
    setSlugManuallyEdited(true);
    setModalOpen(true);
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: slugManuallyEdited ? prev.slug : autoSlug(name),
    }));
  };

  const handleSlugChange = (slug: string) => {
    setSlugManuallyEdited(true);
    setFormData((prev) => ({ ...prev, slug }));
  };

  const handleSave = async () => {
    const slug = formData.slug.trim() || autoSlug(formData.name);
    const payload = {
      name: formData.name.trim(),
      slug,
      description: formData.description.trim() || null,
      color: formData.color,
    };

    try {
      if (editingCategory) {
        await updateMutation.mutateAsync({ id: editingCategory.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setModalOpen(false);
    } catch {
      // Error is displayed by the mutation (React Query surfaces it via isPending/error)
    }
  };

  const handleDeleteClick = (cat: ProductCategoryItem) => {
    setCategoryToDelete(cat);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (categoryToDelete) {
      await deleteMutation.mutateAsync(categoryToDelete.id);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  const productCount = categoryToDelete?._count?.Product ?? 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Product Categories
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Category
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load categories
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Name</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Products</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No categories yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              categories.map((cat) => (
                <TableRow key={cat.id} hover>
                  <TableCell>
                    <ColorSwatch color={cat.color} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={500}>{cat.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                      {cat.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                      {cat.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={cat._count?.Product ?? 0}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 40 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(cat)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(cat)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Modal */}
      <FormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        onSubmit={handleSave}
        loading={isSaving}
        submitLabel={editingCategory ? 'Save Changes' : 'Create Category'}
        submitDisabled={formData.name.trim().length === 0}
      >
        <Stack spacing={2.5}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
            fullWidth
            autoFocus
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="Slug"
            value={formData.slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            fullWidth
            helperText="Used in URLs. Auto-generated from name."
            inputProps={{
              maxLength: 100,
              pattern: '[a-z0-9-]+',
            }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
          />
          <ColorPickerField
            label="Color"
            value={formData.color}
            onChange={(color) => setFormData((p) => ({ ...p, color }))}
            helperText="Optional color for visual identification"
            useSchoolPalette
          />
        </Stack>
      </FormModal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        message={
          productCount > 0
            ? `"${categoryToDelete?.name}" is used by ${productCount} product${productCount === 1 ? '' : 's'}. Deleting it will remove the category from those products.`
            : `Are you sure you want to delete "${categoryToDelete?.name}"?`
        }
        confirmLabel="Delete"
        loading={isDeleting}
        confirmColor="error"
      />
    </Box>
  );
}
